<template>
<!-- eslint-disable max-len -->
<array-input
  :items="narratives"
  :validator="validator"
  :disabled="validator.$invalid && narratives.length > 0"
  :required="required"
  :stub="{ images: [], tooltips: [] }"
  :sections="sections"
>
  <h4 slot="title"><slot v-if="!noTitle">Narratives</slot></h4>

  <template slot="item" slot-scope="{ item: narrative, index: i }">
    <div class="form-row">
      <b-container fluid style="padding: 0px;">
        <b-row>
          <b-col>
            <img
              v-if="narrative.background"
              class="form-image-background"
              :src="resolveAssetPath(narrative.background, 'backgrounds')"
            >
          </b-col>
          <b-col style="display: flex; flex-flow: row nowrap; justify-content: flex-end; align-items: flex-start;">
            <button
              class="btn btn-sm"
              @click.prevent="() => duplicateNarrative(narrative, i)"
            >Duplicate</button>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <h6>Background / Type / Speaker (required)</h6>
    <div class="form-row">
      <b-container fluid style="padding: 5px;">
        <b-row>
          <b-col>
            <checkbox-input
              v-model="narrative.carryoverBackground"
            >
              Carry over previous Background
            </checkbox-input>
            <checkbox-input
              v-if="!noPlayerRole"
              v-model="narrative.playerBackground"
              @input="() => { if (narrative.playerBackground) { narrative.background = ''; } }"
            >
              Player Background
            </checkbox-input>
            <asset-select-input
              v-if="!narrative.playerBackground"
              v-model="narrative.background"
              :validator="validator.$each[i].background"
              required
              autofocus
              legacy-manifest-path="backgrounds"
            >{{narrative.carryoverBackground ? 'Fallback ' : ''}}Background</asset-select-input>
          </b-col>
          <b-col>
            <select-input
              v-model="narrative.type"
              label
              :options="(getSection(i) && getSection(i).types)
                ? getSection(i).types
                : narrativeTypes"
              :display="narrativeTypeToDisplayName"
              :validator="validator.$each[i].type"
              required
              :remove="(requiresSpeaker(narrative.type) || (getSection(i) && getSection(i).noRemove))
                ? null
                : () => { remove(i); }"
              :up="(requiresSpeaker(narrative.type) || (getSection(i) && getSection(i).noReorder))
                ? null
                : () => { up(i); }"
              :down="(requiresSpeaker(narrative.type) || (getSection(i) && getSection(i).noReorder))
                ? null
                : () => { down(i); }"
              @input="onTypeChange(narrative)"
            >
              Type
            </select-input>
          </b-col>
          <b-col v-if="requiresSpeaker(narrative.type)">
            <select-input
              v-if="requiresSpeaker(narrative.type)"
              label
              v-model="narrative.speaker"
              :options="validSpeakerOptions(speakers, narrative.type)"
              :remove="(getSection(i) && getSection(i).noRemove)
                ? null
                : () => { remove(i); }"
              :up="(getSection(i) && getSection(i).noReorder)
                ? null
                : () => { up(i); }"
              :down="(getSection(i) && getSection(i).noReorder)
                ? null
                : () => { down(i); }"
            >
              Speaker
            </select-input>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!-- TODO: may want to generalize this to work with non-bubble decisions too? -->
    <template v-if="narrative.type && (narrative.type.includes('DecisionBubble') || narrative.type.includes('decisionBubble'))">
      <h6>Choice Position Offset (optional)</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="narrative.choiceXOffset"
          placeholder="X Offset"
          no-count
        >
          X Offset (use CSS units) (e.g. 50px)
        </text-input>
        <text-input
          label
          v-model.trim="narrative.choiceYOffset"
          placeholder="Y Offset"
          no-count
        >
          Y Offset (use CSS units) (e.g. 50px)
        </text-input>
      </div>
      <h6>Reason Position Offset (optional)</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="narrative.reasonXOffset"
          placeholder="X Offset"
          no-count
        >
          X Offset (use CSS units) (e.g. 50px)
        </text-input>
        <text-input
          label
          v-model.trim="narrative.reasonYOffset"
          placeholder="Y Offset"
          no-count
        >
          Y Offset (use CSS units) (e.g. 50px)
        </text-input>
      </div>
    </template>

    <div class="form-row">
      <template v-if="!(['enterLocation', 'exitLocation', 'animation', 'exposition'].includes(narrative.type))">
        <checkbox-input class="col-sm-3" v-model="narrative.showNavbar">
          Show Dashboard (Top Bar)
        </checkbox-input>

        <checkbox-input v-if="narrative.showNavbar" class="col-sm-2" v-model="narrative.showMetrics">
          Show Metrics
        </checkbox-input>
      </template>

      <checkbox-input v-if="['dialog', 'leftDecisionBubble', 'rightDecisionBubble'].includes(narrative.type) && (['left', 'right', 'extra-left', 'extra-right'].includes(narrative.speaker))" class="col-sm-2" v-model="narrative.thoughtBubble">
        Thought Bubble
      </checkbox-input>
    </div>

    <text-input label v-if="narrative.showNavbar" v-model.trim="narrative.navbarText">Dashboard (Top Bar) Text</text-input>

    <collapsible-section
      v-if="
        ['dialog', 'leftVideo', 'rightVideo', 'leftDecisionBubble', 'rightDecisionBubble', 'leftDecision', 'rightDecision'].includes(narrative.type)
        || (['animation', 'exposition'].includes(narrative.type) || (narrative.speaker === 'center' && ['dialog', 'team', 'leftDecisionBubble', 'rightDecisionBubble'].includes(narrative.type)))
      "
      name="Characters"
      :id="`${($slots.default && $slots.default[0].text || 'Narrative').replace(/\s/g, '-')}-${i}-Characters`"
      bold
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <template v-if="['dialog', 'leftVideo', 'rightVideo', 'leftDecisionBubble', 'rightDecisionBubble', 'leftDecision', 'rightDecision'].includes(narrative.type)">
        <template v-if="!(['rightVideo', 'rightDecision'].includes(narrative.type))">
          <h6>Left Character</h6>
          <div class="form-row">
            <div class="col-sm-2">
              <checkbox-input
                v-if="!noPlayerAvatar && !(narrative.leftNarrator)"
                v-model="narrative.leftPlayer"
                :disabled="['leftDecisionBubble', 'leftDecision'].includes(narrative.type)"
                @input="() => { if (narrative.leftPlayer || narrative.leftNarrator) { narrative.leftCharacter = null; } }"
              >
                Left Player
              </checkbox-input>
              <checkbox-input
                v-if="!(['leftDecisionBubble', 'leftDecision'].includes(narrative.type)) && !(narrative.leftPlayer)"
                v-model="narrative.leftNarrator"
                @input="() => { if (narrative.leftPlayer || narrative.leftNarrator) { narrative.leftCharacter = null; } }"
              >
                Left Narrator
              </checkbox-input>
            </div>
            <div class="col-sm-3">
              <character-select-input
                select-only-id
                v-if="!(narrative.leftPlayer || narrative.leftNarrator)"
                v-model="narrative.leftCharacter"
                :default-filters="{narrator: true}"
                use="_id"
                display="name"
                :to="(id) => { return `/blade/characters/${id}/edit`; }"
              >
                Left Character
              </character-select-input>
            </div>
            <div class="col-sm-6">
              <div style="display: flex;">
                <select-input
                  class="col-sm-7"
                  v-model="narrative.leftVariant"
                  :options="variantOptions(narrative.leftCharacter, (narrative.leftPlayer || narrative.leftNarrator))"
                >
                  Left Variant
                </select-input>

                <b-button
                  v-b-toggle.collapse-loff
                  size="sm"
                  style="height: 20px;"
                >
                  <strong>Additional Left Offset Settings</strong>
                </b-button>
              </div>
              <h6>Left Extra Variant (optional)</h6>
              <select-input
                class="col-sm-7"
                v-model="narrative.leftExtraVariant"
                :options="variantOptions(narrative.leftCharacter, (narrative.leftPlayer || narrative.leftNarrator))"
              >
                Left Extra Variant
              </select-input>
            </div>
          </div>
          <b-collapse id="collapse-loff" class="form-row form-drawer">
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.leftXOffset"
              placeholder="X Offset (e.g. 50px)"
              no-count
            >
              X Offset (CSS units)
            </text-input>
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.leftYOffset"
              placeholder="Y Offset (e.g. 50px)"
              no-count
            >
              Y Offset (CSS units)
            </text-input>
            <number-input
              label
              class="col-sm-3"
              v-model.trim="narrative.leftOrderOverride"
            >
              Z Order Override
            </number-input>
          </b-collapse>
        </template>
        <template v-if="!(['leftVideo', 'leftDecision'].includes(narrative.type))">
          <h6>Right Character</h6>
          <div class="form-row">
            <div class="col-sm-2">
              <checkbox-input
                v-if="!noPlayerAvatar && !(narrative.rightNarrator)"
                v-model="narrative.rightPlayer"
                :disabled="['rightDecisionBubble', 'rightDecision'].includes(narrative.type)"
                @input="() => { if (narrative.rightPlayer || narrative.rightNarrator) { narrative.rightCharacter = null; } }"
              >
                Right Player
              </checkbox-input>
              <checkbox-input
                v-if="!(['rightDecisionBubble', 'rightDecision'].includes(narrative.type)) && !(narrative.rightPlayer)"
                v-model="narrative.rightNarrator"
                @input="() => { if (narrative.rightPlayer || narrative.rightNarrator) { narrative.rightCharacter = null; } }"
              >
                Right Narrator
              </checkbox-input>
            </div>

            <div class="col-sm-3">
              <character-select-input
                select-only-id
                v-if="!(narrative.rightPlayer || narrative.rightNarrator)"
                v-model="narrative.rightCharacter"
                :default-filters="{narrator: true}"
                use="_id"
                display="name"
                :to="(id) => { return `/blade/characters/${id}/edit`; }"
              >
                Right Character
              </character-select-input>
            </div>
            <div class="col-sm-6">
              <div style="display: flex;">
                <select-input
                  class="col-sm-7"
                  v-model="narrative.rightVariant"
                  :options="variantOptions(narrative.rightCharacter, (narrative.rightPlayer || narrative.rightNarrator))"
                >
                  Right Variant
                </select-input>

                <b-button v-b-toggle.collapse-roff size="sm" style="height: 20px;">
                  <strong>Additional Right Offset Settings</strong>
                </b-button>
              </div>
              <h6>Right Extra Variant (optional)</h6>
              <select-input
                  class="col-sm-7"
                  v-model="narrative.rightExtraVariant"
                  :options="variantOptions(narrative.rightCharacter, (narrative.rightPlayer || narrative.rightNarrator))"
                >
                  Right Extra Variant
                </select-input>
            </div>
          </div>
          <b-collapse id="collapse-roff" class="form-row form-drawer">
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.rightXOffset"
              placeholder="X Offset (e.g. 50px)"
              no-count
            >
              X Offset (CSS units)
            </text-input>
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.rightYOffset"
              placeholder="Y Offset (e.g. 50px)"
              no-count
            >
              Y Offset (CSS units)
            </text-input>
            <number-input
              label
              class="col-sm-3"
              v-model.trim="narrative.rightOrderOverride"
            >
              Z Order Override
            </number-input>
          </b-collapse>
        </template>
        <template v-if="['dialog'].includes(narrative.type)">
          <div style="margin-bottom: 10px; font-size: 80%; line-height: 134%;">
            <b-button v-b-toggle.collapse-1 size="sm">
              <strong>Things To Note Regarding Extra Characters</strong>
            </b-button>
            <b-collapse id="collapse-1">
              <div style="border: 1px solid lightgrey; border-radius: 4px; padding: 0 6px 6px; margin-top: -9px;">
                <br/>· Extra Characters' position offsets are relative to the position of the non-extra character on that side and this is&nbsp;<strong>additive</strong>&nbsp;with any offset specified for the image variant selected.
                For X Offsets, this is the additional distance&nbsp;<strong>away</strong>&nbsp;from the side the character appears.
                <br/>· Extra Characters' position offsets also add to the speech bubble offsets if that character is the one speaking.
                Any difference in a character's position offset and their speech bubble offset should be indicated via the image variant settings.
                In general, these position offsets should be used only when the position needs to be changed slightly from the image variant default in the particular scene to accommodate the other character sharing that side.
                <br/>· Extra Characters are ordered on top of the character with which they share the side.
              </div>
            </b-collapse>
          </div>
          <div class="form-row">
            <div class="col-sm-2">
              <checkbox-input
                v-if="!noPlayerAvatar && !(narrative.extraLeftNarrator)"
                v-model="narrative.extraLeftPlayer"
                :disabled="['leftDecisionBubble', 'leftDecision'].includes(narrative.type)"
                @input="() => { if (narrative.extraLeftPlayer || narrative.extraLeftNarrator) { narrative.extraLeftCharacter = null; } }"
              >
                Extra Left Player
              </checkbox-input>
              <checkbox-input
                v-if="!(['leftDecisionBubble', 'leftDecision'].includes(narrative.type)) && !(narrative.extraLeftPlayer)"
                v-model="narrative.extraLeftNarrator"
                @input="() => { if (narrative.extraLeftPlayer || narrative.extraLeftNarrator) { narrative.extraLeftCharacter = null; } }"
              >
                Extra Left Narrator
              </checkbox-input>
            </div>

            <div class="col-sm-3">
              <character-select-input
                select-only-id
                v-if="!(narrative.extraLeftPlayer || narrative.extraLeftNarrator)"
                label
                v-model="narrative.extraLeftCharacter"
                :default-filters="{narrator: true}"
                use="_id"
                display="name"
                :to="(id) => { return `/blade/characters/${id}/edit`; }"
              >
                Extra Left Character
              </character-select-input>
            </div>
            <div class="col-sm-6">
              <div style="display: flex;">
                <select-input
                  class="col-sm-7"
                  label
                  v-model="narrative.extraLeftVariant"
                  :options="variantOptions(narrative.extraLeftCharacter, (narrative.extraLeftPlayer || narrative.extraLeftNarrator))"
                >
                  Extra Left Variant
                </select-input>

                <b-button v-b-toggle.collapse-xloff size="sm" style="height: 20px; margin-top: 32px;">
                  <strong>Additional Extra Left Offset Settings</strong>
                </b-button>
              </div>
              <template v-if="narrative.extraLeftCharacter || narrative.extraLeftPlayer">
                <h6>Extra Left Extra Variant (optional)</h6>
                <select-input
                  class="col-sm-7"
                  v-model="narrative.extraLeftExtraVariant"
                  :options="variantOptions(narrative.extraLeftCharacter, (narrative.extraLeftPlayer || narrative.extraLeftNarrator))"
                >
                  Extra Left Extra Variant
                </select-input>
              </template>
            </div>
          </div>
          <b-collapse id="collapse-xloff" class="form-row form-drawer">
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.extraLeftXOffset"
              placeholder="X Offset (e.g. 50px)"
              no-count
            >
              X Offset (CSS units)
            </text-input>
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.extraLeftYOffset"
              placeholder="Y Offset (e.g. 50px)"
              no-count
            >
              Y Offset (CSS units)
            </text-input>
            <number-input
              label
              class="col-sm-3"
              v-model.trim="narrative.extraLeftOrderOverride"
            >
              Z Order Override
            </number-input>
          </b-collapse>
          <div class="form-row">
            <div class="col-sm-2">
              <checkbox-input
                v-if="!noPlayerAvatar && !(narrative.extraRightNarrator)"
                v-model="narrative.extraRightPlayer"
                :disabled="['rightDecisionBubble', 'rightDecision'].includes(narrative.type)"
                @input="() => { if (narrative.extraRightPlayer || narrative.extraRightNarrator) { narrative.extraRightCharacter = null; } }"
              >
                Extra Right Player
              </checkbox-input>
              <checkbox-input
                v-if="!(['rightDecisionBubble', 'rightDecision'].includes(narrative.type)) && !(narrative.extraRightPlayer)"
                v-model="narrative.extraRightNarrator"
                @input="() => { if (narrative.extraRightPlayer || narrative.extraRightNarrator) { narrative.extraRightCharacter = null; } }"
              >
                Extra Right Narrator
              </checkbox-input>
            </div>

            <div class="col-sm-3">
              <character-select-input
                select-only-id
                v-if="!(narrative.extraRightPlayer || narrative.extraRightNarrator)"
                label
                v-model="narrative.extraRightCharacter"
                :default-filters="{narrator: true}"
                use="_id"
                display="name"
                :to="(id) => { return `/blade/characters/${id}/edit`; }"
              >
                Extra Right Character
              </character-select-input>
            </div>
            <div class="col-sm-6">
              <div style="display: flex;">
                <select-input
                  class="col-sm-7"
                  label
                  v-model="narrative.extraRightVariant"
                  :options="variantOptions(narrative.extraRightCharacter, (narrative.extraRightPlayer || narrative.extraRightNarrator))"
                >
                  Extra Right Variant
                </select-input>

                <b-button v-b-toggle.collapse-xroff size="sm" style="height: 20px; margin-top: 32px;">
                  <strong>Additional Extra Right Offset Settings</strong>
                </b-button>
              </div>
              <template v-if="narrative.extraRightCharacter || narrative.extraRightPlayer">
                <select-input
                  class="col-sm-7"
                  label
                  v-model="narrative.extraRightExtraVariant"
                  :options="variantOptions(narrative.extraRightCharacter, (narrative.extraRightPlayer || narrative.extraRightNarrator))"
                >
                  Extra Right Variant
                </select-input>
              </template>
            </div>
          </div>
          <b-collapse id="collapse-xroff" class="form-row form-drawer">
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.extraRightXOffset"
              placeholder="X Offset (e.g. 50px)"
              no-count
            >
              X Offset (CSS units)
            </text-input>
            <text-input
              label
              class="col-sm-3"
              v-model.trim="narrative.extraRightYOffset"
              placeholder="Y Offset (e.g. 50px)"
              no-count
            >
              Y Offset (CSS units)
            </text-input>
            <number-input
              label
              class="col-sm-3"
              v-model.trim="narrative.extraRightOrderOverride"
            >
              Z Order Override
            </number-input>
          </b-collapse>
        </template>
        <checkbox-input v-model="narrative.noAnimation">
          Disable Character Animations
        </checkbox-input>
        <checkbox-input v-model="narrative.noAnimationEnter">
          Disable Character Animations Entering
        </checkbox-input>
        <checkbox-input v-model="narrative.noAnimationLeave">
          Disable Character Animations Leaving
        </checkbox-input>
      </template>

      <template v-if="['animation', 'exposition'].includes(narrative.type) || (narrative.speaker === 'center' && ['dialog', 'team', 'leftDecisionBubble', 'rightDecisionBubble'].includes(narrative.type))">
        <h6>Center Character</h6>
        <div class="form-row">
          <character-select-input
            v-if="!(narrative.centerPlayer || narrative.centerNarrator)"
            class="col-sm-3"
            v-model="narrative.centerCharacter"
            select-only-id
            :default-filters="['animation', 'exposition'].includes(narrative.type) ? { challengeIntro: true } : { narrator: true }"
            use="_id"
            :validator="validator.$each[i].centerCharacter"
            display="name"
            :to="(id) => { return `/blade/characters/${id}/edit`; }"
            required
          >
            Center Character (required)
          </character-select-input>

          <select-input
            class="col-sm-3"
            v-model="narrative.centerVariant"
            :options="variantOptions(narrative.centerCharacter, (narrative.centerPlayer || narrative.centerNarrator))"
          >
            Center Variant
          </select-input>

          <div class="col-sm-3">
            <checkbox-input
              v-if="!noPlayerAvatar && !(['animation', 'exposition'].includes(narrative.type)) && !(narrative.centerNarrator)"
              v-model="narrative.centerPlayer"
            >
              Center Player
            </checkbox-input>
            <checkbox-input
              v-if="!(['animation', 'exposition'].includes(narrative.type)) && !(narrative.centerPlayer)"
              v-model="narrative.centerNarrator"
            >
              Center Narrator
            </checkbox-input>
          </div>
        </div>
      </template>
    </collapsible-section>

    <template v-if="['fullVideo', 'leftVideo', 'rightVideo'].includes(narrative.type)">
      <video v-if="narrative.video" class="form-video" :src="resolveAssetPath(narrative.video, 'videos')" controls playsinline></video>
      <asset-select-input label v-model="narrative.video" :validator="validator.$each[i].video" required>Video</asset-select-input>
    </template>

    <checkbox-input
      v-if="['animation', 'exposition', 'enterLocation', 'exitLocation'].includes(narrative.type)"
      v-model="narrative.invertBackgroundlessTextColor"
    >
      Change Any Backgroundless Text Color To Black <span style="font-size: 85%;">(use this if there is a readability problem)</span>
    </checkbox-input>

    <template
      v-if="['animation', 'exposition', 'dialog'].includes(narrative.type)"
    >
      <text-input
        label
        v-model.trim="narrative.optionalText"
      >
        {{(['dialog'].includes(narrative.type)) ? 'Response (optional)' : 'Optional Text'}}
      </text-input>
      <voice-line-input
        :text="narrative.optionalText"
        :data-object="narrative"
        field-name="optionalTextVoiceLine"
        :character="
          (['animation', 'exposition'].includes(narrative.type) || narrative.responder === 'center')
            ? narrative.centerCharacter
            : ((narrative.responder === 'left')
              ? narrative.leftCharacter
              : ((narrative.responder === 'right')
                ? narrative.rightCharacter
                : ((narrative.responder === 'extraLeft')
                  ? narrative.extraLeftCharacter
                  : ((narrative.responder === 'extraRight')
                    ? narrative.extraRightCharacter
                    : ''))))
        "
      ></voice-line-input>
    </template>

    <select-input
      v-if="requiresSpeaker(narrative.type) && ['dialog'].includes(narrative.type)"
      label
      v-model="narrative.responder"
      :options="validResponderOptions(speakers, narrative.type)"
    >
      Responder (optional)
    </select-input>

    <collapsible-section
      v-if="
        (
          narrative.type
          && requiresSpeaker(narrative.type)
          && ['dialog'].includes(narrative.type)
        )
        || (
          narrative.type
          && ['dialog', 'leftDecisionBubble', 'rightDecisionBubble', 'exposition', 'animation'].includes(narrative.type)
        )
      "
      name="Bubble/Response/Panel Position Offsets"
      :id="`${($slots.default && $slots.default[0].text || 'Narrative').replace(/\s/g, '-')}-${i}-BRPO`"
      :buttonStyle="{
        'font-size': '120%'
      }"
      bold
    >
      <template v-if="narrative.type && requiresSpeaker(narrative.type) && ['dialog'].includes(narrative.type)">
        <h6>Response Position Offset (optional)</h6>
        <div style="display: flex; flex-direction: row;">
          <text-input
            label
            v-model.trim="narrative.responseXOffset"
            placeholder="X Offset"
            no-count
          >
            X Offset (use CSS units) (e.g. 50px)
          </text-input>
          <text-input
            label
            v-model.trim="narrative.responseYOffset"
            placeholder="Y Offset"
            no-count
          >
            Y Offset (use CSS units) (e.g. 50px)
          </text-input>
        </div>
      </template>
      <template v-if="narrative.type && ['dialog', 'leftDecisionBubble', 'rightDecisionBubble'].includes(narrative.type)">
        <h6>Speech Bubble Position Offset (optional)</h6>
        <div style="display: flex; flex-direction: row;">
          <text-input
            label
            v-model.trim="narrative.bubbleXOffset"
            placeholder="X Offset"
            no-count
          >
            X Offset (use CSS units) (e.g. 50px)
          </text-input>
          <text-input
            label
            v-model.trim="narrative.bubbleYOffset"
            placeholder="Y Offset"
            no-count
          >
            Y Offset (use CSS units) (e.g. 50px)
          </text-input>
        </div>
      </template>
      <template v-if="
        (narrative.type && ['dialog'].includes(narrative.type) && ['center'].includes(narrative.speaker))
        || (narrative.type && ['exposition', 'animation'].includes(narrative.type))
      ">
        <h6>Panel Position Offset (optional)</h6>
        <div style="display: flex; flex-direction: row;">
          <text-input
            label
            v-model.trim="narrative.panelXOffset"
            placeholder="X Offset"
            no-count
          >
            X Offset (use CSS units) (e.g. 50px)
          </text-input>
          <text-input
            label
            v-model.trim="narrative.panelYOffset"
            placeholder="Y Offset"
            no-count
          >
            Y Offset (use CSS units) (e.g. 50px)
          </text-input>
        </div>
      </template>
    </collapsible-section>

    <template
      v-if="
        !(['leftDecision', 'rightDecision'].includes(narrative.type))
        && !(narrative.type === 'team' && narrative.speaker !== 'center')
      "
    >
      <text-input
        label
        v-model.trim="narrative.text"
        :validator="validator.$each[i].text"
        required
        :textarea="!(['enterLocation', 'exitLocation', 'leftDecision', 'rightDecision'].includes(narrative.type))"
      >
        Text (required)
      </text-input>

      <voice-line-input
        :text="narrative.text"
        :data-object="narrative"
        field-name="textVoiceLine"
        :character="
          (narrative.speaker === 'center')
            ? narrative.centerCharacter
            : ((narrative.speaker === 'left')
              ? narrative.leftCharacter
              : ((narrative.speaker === 'right')
                ? narrative.rightCharacter
                : ((narrative.speaker === 'extraLeft')
                  ? narrative.extraLeftCharacter
                  : ((narrative.speaker === 'extraRight')
                    ? narrative.extraRightCharacter
                    : ''))))
        "
      ></voice-line-input>
    </template>

    <asset-select-input
      v-if="
        ['dialog', 'animation', 'exposition', 'enterLocation', 'exitLocation', 'leftVideo', 'rightVideo'].includes(narrative.type)
      "
      label
      v-model="narrative.textSound"
    >
      Sound Played When Text Appears
    </asset-select-input>

    <collapsible-section
      name="Background Audio Change Settings"
      :id="`${($slots.default && $slots.default[0].text || 'Narrative').replace(/\s/g, '-')}-${i}-BACS`"
      :buttonStyle="{
        'font-size': '120%'
      }"
      bold
    >
      <asset-select-input
        label
        v-model="narrative.backgroundAudioChange"
      >
        Background Audio Change
      </asset-select-input>
      <checkbox-input
        v-if="narrative.backgroundAudioChange"
        v-model="narrative.backgroundAudioLoopingChange"
      >
        Background Audio Change Should Loop
      </checkbox-input>
      <number-input
        label
        v-model="narrative.backgroundAudioVolumeChange"
      >
        Background Audio Volume Change (percentage, 1 - 100, set negative to mute, 0 is unchanged)
      </number-input>
    </collapsible-section>

    <checkbox-input
      v-if="['dialog'].includes(narrative.type)"
      v-model="narrative.noWaitForIFrames"
    >
      Don't Force Wait For IFrames To Load
    </checkbox-input>

    <text-input
      label
      v-model.trim="narrative.nextButtonText"
    >
      Next Button Text (leave blank for default)
    </text-input>

    <collapsible-section
      v-if="['fullVideo'].includes(narrative.type)"
      name="Button Style Settings"
      :id="`${($slots.default && $slots.default[0].text || 'Narrative').replace(/\s/g, '-')}-${i}-BSS`"
      :buttonStyle="{
        'font-size': '120%'
      }"
      bold
    >
      <text-input
        label
        v-model.trim="narrative.buttonXOffset"
        no-count
      >
        Button X Offset (uses CSS units; doesn't affect back button in native mode)
      </text-input>

      <text-input
        label
        v-model.trim="narrative.buttonYOffset"
        no-count
      >
        Button Y Offset (uses CSS units; doesn't affect back button in native mode)
      </text-input>

      <div
        v-if="narrative.buttonColor != null && narrative.buttonColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${narrative.buttonColor};`"
      />
      <text-input
        label
        v-model.trim="narrative.buttonColor"
        no-count
      >
        Button Color (overrides branding, supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="narrative.buttonColorLightened != null && narrative.buttonColorLightened.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${narrative.buttonColorLightened};`"
      />
      <text-input
        label
        v-model.trim="narrative.buttonColorLightened"
        no-count
      >
        Button Color Lightened (overrides branding, supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="narrative.buttonTextColor != null && narrative.buttonTextColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${narrative.buttonTextColor};`"
      />
      <text-input
        label
        v-model.trim="narrative.buttonTextColor"
        no-count
      >
        Button Text Color (overrides branding, supports all valid CSS colors, but rgba() recommended)
      </text-input>
    </collapsible-section>

    <text-input
      label
      v-model.trim="narrative.backButtonText"
    >
      Back Button Text (leave blank for default)
    </text-input>

    <checkbox-input v-model="narrative.forceEnableBack">
      Force Enable Back (overrides Gamulation)
    </checkbox-input>

    <checkbox-input v-model="narrative.preventEnableBack">
      Prevent Enable Back (overrides Gamulation)
    </checkbox-input>

    <array-input
      v-if="['dialog', 'enterLocation', 'exitLocation'].includes(narrative.type)"
      class="offset-sm-1"
      :items="narrative.images"
      :stub="{ image: '', description: '' }"
      :disabled="['enterLocation', 'exitLocation'].includes(narrative.type) ? (narrative.images && narrative.images.length > 0) : false"
    >
      <h4 slot="title">{{['enterLocation', 'exitLocation'].includes(narrative.type) ? 'Image' : 'Images'}}</h4>

      <template slot="item" slot-scope="{ item: image, index: i }">
        <img
          v-if="image.image"
          class="form-image-background"
          :src="resolveAssetPath(image.image, 'images')"
        />
        <asset-select-input
          :label="['dialog'].includes(narrative.type)"
          v-model="image.image"
          required
          autofocus
        >
          Image
        </asset-select-input>
        <text-input
          v-if="['dialog'].includes(narrative.type)"
          label
          v-model.trim="image.description"
          :remove="() => { narrative.images.splice(i, 1); }"
        >
          Description
        </text-input>
        <button
          v-if="!(['dialog'].includes(narrative.type))"
          class="btn"
          @click.prevent="narrative.images.splice(i, 1)"
        >
          Remove Image
        </button>
        <div style="height: 1px; width: 100%; background-color: rgba(60, 60, 60, 1);"/>
      </template>
      <span slot="button">Add Image</span>
    </array-input>

    <collapsible-section
      name="Tooltips"
      :id="`${($slots.default && $slots.default[0].text || 'Narrative').replace(/\s/g, '-')}-${i}-Tooltips`"
      bold
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <tooltip-input
        :tooltips="narrative.tooltips"
        :target-types="getTooltipTargetTypesForNarrativeType(narrative.type)"
        no-title
      >Tooltips</tooltip-input>
    </collapsible-section>

    <checkbox-input
      v-if="['fullVideo', 'leftVideo', 'rightVideo'].includes(narrative.type)"
      v-model="narrative.isReplayable"
    >
      Replayable (Optional)
    </checkbox-input>

    <hr v-if="i < narratives.length - 1" style="border: 1px solid #aaa;">
  </template>
  <span slot="button">Add {{ $slots.default && $slots.default[0].text || 'Narrative' }}</span>
</array-input>
</template>

<script>
import ArrayInput from '../inputs/ArrayInput';
import CheckboxInput from '../inputs/CheckboxInput';
import AssetSelectInput from '../inputs/AssetSelectInput';
import CharacterSelectInput from '../inputs/CharacterSelectInput';
import NumberInput from '../inputs/NumberInput';
import SelectInput from '../inputs/SelectInput';
import TextInput from '../inputs/TextInput';
import CollapsibleSection from './CollapsibleSection';
import TooltipInput from './TooltipInput';
import VoiceLineInput from './VoiceLineInput';

export default {
  props: {
    narratives: { type: Array, required: true },
    validator: { type: Object, required: true },
    characters: { type: Array, required: true },
    variants: { type: Array, required: true },
    narrativeTypes: { type: Array, required: true },
    speakers: { type: Array, required: true },
    required: { type: Boolean },
    noPlayerAvatar: { type: Boolean },
    noPlayerRole: { type: Boolean },
    noTitle: { type: Boolean },
    sections: { type: Array },
  },
  components: {
    ArrayInput,
    CheckboxInput,
    CollapsibleSection,
    TooltipInput,
    AssetSelectInput,
    CharacterSelectInput,
    NumberInput,
    SelectInput,
    VoiceLineInput,
    TextInput
  },
  data() {
    return {
    };
  },
  computed: {
    narrators() {
      return this.characters.filter(c => c.narrator);
    },
    challengeIntroCharacters() {
      return this.characters.filter(c => c.challengeIntro);
    }
  },
  methods: {
    up(index) {
      if (index <= 0) return;
      const [item] = this.narratives.splice(index, 1);
      this.narratives.splice(index - 1, 0, item);
    },
    down(index) {
      if (index >= this.narratives.length - 1) return;
      const [item] = this.narratives.splice(index, 1);
      this.narratives.splice(index + 1, 0, item);
    },
    remove(index) {
      this.narratives.splice(index, 1);
    },
    duplicateNarrative(narrative, index) {
      const newNarrative = Object.assign({}, narrative);
      newNarrative.textVoiceLine = null;
      newNarrative.optionalTextVoiceLine = null;
      this.narratives.splice(index, 0, newNarrative);
    },
    requiresSpeaker(narrativeType) {
      return [
        'dialog',
        'team',
        'leftDecisionBubble',
        'rightDecisionBubble'
      ].includes(narrativeType);
    },
    variantOptions(characterId, isPlayerOrNarrator = false) {
      if (!isPlayerOrNarrator) {
        const c = this.characters.find(ch => ch._id === characterId);
        if (c) {
          if (c.imageVariants && c.imageVariants.length > 0) {
            return c.imageVariants.map(v => v.name);
          }
          return [];
        }
      }
      return this.variants;
    },
    validSpeakerOptions(speakers, type) {
      if (type === 'team') {
        return speakers.filter(s => s === 'center');
      }
      return speakers;
    },
    // eslint-disable-next-line no-unused-vars
    validResponderOptions(speakers, type) {
      return speakers.filter(s => s !== 'center');
    },
    narrativeTypeToDisplayName(t) {
      if (t === 'leadershipReport') {
        return 'Report: TM2';
      } else if (t === 'leadershipReportLegacy') {
        return 'Report: TM1';
      } else if (t === 'leadershipReportSF1.0') {
        return 'Report: Old Providence';
      } else if (t === 'almasReport') {
        return 'Report: Almas';
      } else if (t === 'almas2Report') {
        return 'Report: Almas HCA2';
      } else if (t === 'deloitteReport') {
        return 'Report: Deloitte';
      } else if (t === 'reportHPG') {
        return 'Report: HPG';
      } else if (t === 'reportHPGS') {
        return 'Report: HPGS';
      } else if (t === 'reportSGM') {
        return 'Report: SGM';
      } else if (t === 'animation') {
        return 'Exposition: Animated Background';
      } else if (t === 'corpReportHSG') {
        return 'Report: Corp HSG';
      } else if (t === 'dialog') {
        return 'Dialogue';
      } else if (t === 'enterLocation') {
        return 'Location: Enter';
      } else if (t === 'entrepreneurshipReadinessReport') {
        return 'Report: Entrepreneurship Readiness';
      } else if (t === 'exitLocation') {
        return 'Location: Exit';
      } else if (t === 'exposition') {
        return 'Exposition: Static Background';
      } else if (t === 'fullVideo') {
        return 'Video: Full Screen';
      } else if (t === 'leftVideo') {
        return 'Video: Left Character';
      } else if (t === 'rightVideo') {
        return 'Video: Right Character';
      } else if (t === 'studentReport') {
        return 'Report: Student (deprecated alias of Student HSG)';
      } else if (t === 'studentReportADT') {
        return 'Report: Student ADT';
      } else if (t === 'studentReportALG') {
        return 'Report: Student ALG';
      } else if (t === 'studentReportHSG') {
        return 'Report: Student HSG';
      } else if (t === 'certificatePHN') {
        return 'Report: PHN Certificates';
      } else if (t === 'team') {
        return 'Team';
      } else if (t === 'leftDecision') {
        return 'Decision: Left Character';
      } else if (t === 'rightDecision') {
        return 'Decision: Right Character';
      } else if (t === 'leftDecisionBubble') {
        return 'Decision Bubble: Left Character';
      } else if (t === 'rightDecisionBubble') {
        return 'Decision Bubble: Right Character';
      }
      return t;
    },
    onTypeChange(n) {
      if (n && n.type) {
        if (['leftDecisionBubble', 'leftDecision'].includes(n.type)) {
          n.leftPlayer = true;
          n.leftNarrator = false;
          n.leftCharacter = null;
        } else if (['rightDecisionBubble', 'rightDecision'].includes(n.type)) {
          n.rightPlayer = true;
          n.rightNarrator = false;
          n.rightCharacter = null;
        }
      }
    },
    getSection(i) {
      return this.sections ? this.sections.find(s => s.start <= i && s.end > i) : null;
    },
    getTooltipTargetTypesForNarrativeType(ntype) {
      if (ntype && !ntype) return;
      // TODO
      return [
        'speechBubble',
        'responseBubble',
        'characterLeft',
        'characterExtraLeft',
        'characterRight',
        'characterExtraRight',
        'videoOrImage',
      ];
    },
    initNarrative(n) {
      if (n) {
        if (n.images == null) {
          n.images = [];
        }

        if (n.type) {
          if (['leftDecisionBubble', 'leftDecision'].includes(n.type)) {
            n.leftPlayer = true;
            n.leftCharacter = null;
          } else if (['rightDecisionBubble', 'rightDecision'].includes(n.type)) {
            n.rightPlayer = true;
            n.rightCharacter = null;
          }
        }
      }
    }
  },
  created() {
    // console.log(this.narratives);
    // this.narratives.forEach((n) => { console.log(n.images); });
    this.narratives.forEach(this.initNarrative);
    // this.narratives.forEach((n) => { console.log(n.images); });
  },
  mounted() {
    // console.log(this.narratives);
    // this.narratives.forEach((n) => { console.log(n.images); });
    this.narratives.forEach(this.initNarrative);
    // this.narratives.forEach((n) => { console.log(n.images); });
  },
  watch: {
    narratives() {
      // console.log(this.narratives);
      // this.narratives.forEach((n) => { console.log(n.images); });
      this.narratives.forEach(this.initNarrative);
      // this.narratives.forEach((n) => { console.log(n.images); });
    }
  }
};
</script>

<style lang="scss" scoped>
.form-drawer {
  margin: 0 200px 10px 1px;
  padding-left: 20px;
  border-left: 1px solid lightgrey;
  border-bottom: 1px solid lightgrey;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/inputs/NarrativeInput.vue